"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, House as Home, ShieldCheck, ArrowRight, Hammer, CircleCheck as CheckCircle2, MapPin, Clock, Umbrella, ShieldAlert } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const coverageAreas = [{
  name: "Town centre commercial",
  desc: "Retail & high-street shopfronts"
}, {
  name: "Office developments",
  desc: "Business parks & corporate sites"
}, {
  name: "Residential suburbs",
  desc: "Housing estates & private homes"
}, {
  name: "Refurbishment zones",
  desc: "Mixed-use & listed buildings"
}, {
  name: "Roofing works",
  desc: "Domestic & commercial roof access"
}];
const residentialFeatures = ["Roof repairs, re-tiling, and guttering access", "Single and multi-storey home extensions", "Safe working platforms for chimney rebuilds and cowls", "Full facade access for rendering, cladding, and repointing"];
const commercialFeatures = ["Fully managed packages for main contractors and developers", "Town-centre commercial refurbishments and shopfitting", "Occupied premises with minimal operational disturbance", "Compliant TG20:21 design calculations and risk assessments"];
const roofingTradeFeatures = [{
  title: "Roofing Scaffolding",
  description: "Stable, multi-level working decks tailored for strip-and-re-roofing, solar arrays, and fascia renewals.",
  to: "/RoofingScaffoldingPage",
  icon: Hammer
}, {
  title: "Temporary Roofs",
  description: "Complete weather-protection canopies ensuring works proceed regardless of rainfall or adverse conditions.",
  to: "/TemporaryRoofsPage",
  icon: Umbrella
}, {
  title: "Edge Protection",
  description: "Guardrails, debris netting, and safety toe-boards installed to strict HSE and NASC standards.",
  to: "/EdgeProtectionPage",
  icon: ShieldAlert
}];
function WycombeCoverageContent() {
  const shouldReduceMotion = useReducedMotion();
  const {
    posts,
    loading
  } = usePosts();
  const residentialPhoto = posts.find(p => p.title === "High Wycombe Residential Scaffolding Image" || p.customFields?.photo_name === "High Wycombe Residential Scaffolding Image");
  const commercialPhoto = posts.find(p => p.title === "High Wycombe Commercial Scaffolding Image" || p.customFields?.photo_name === "High Wycombe Commercial Scaffolding Image");
  const resImgSrc = residentialPhoto?.customFields?.image || residentialPhoto?.featuredImage;
  const resAlt = residentialPhoto?.customFields?.alt_text || "Residential scaffolding installation on a domestic house in High Wycombe";
  const commImgSrc = commercialPhoto?.customFields?.image || commercialPhoto?.featuredImage;
  const commAlt = commercialPhoto?.customFields?.alt_text || "Commercial scaffolding setup on High Wycombe town centre building";
  const itemAnimation = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const
      }
    }
  };
  return <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
      {/* Header Block */}
      <div className="max-w-3xl mb-12 md:mb-16">
        <div className="flex items-center gap-2 text-primary font-medium text-sm mb-3">
          <MapPin className="size-4" />
          <span>High Wycombe & South Buckinghamshire</span>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground mb-4">
          Scaffolding coverage in High Wycombe
        </h2>
        <p className="text-lg text-muted-foreground leading-relaxed">
          BMF Scaffolding Ltd provides safety-led scaffolding across High Wycombe,
          servicing town-centre commercial properties, office developments,
          private residences, and specialist roofing projects with full NASC
          and CHAS compliance.
        </p>
      </div>

      {/* Coverage Highlights Pills & Postcode/Nearby Areas */}
      <div className="mb-14 p-4 sm:p-6 bg-muted/60 rounded-xl border border-border space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Covered locations and property types
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {coverageAreas.map((area, index) => <div key={area.name} data-index={index} className="bg-card p-3 rounded-lg border border-border flex flex-col justify-between">
                <span className="font-semibold text-sm text-foreground">
                  {area.name}
                </span>
                <span className="text-xs text-muted-foreground mt-1">
                  {area.desc}
                </span>
              </div>)}
          </div>
        </div>

        {/* Postcodes and Nearby Towns Block */}
        <div className="pt-5 border-t border-border grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-6 space-y-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
              High Wycombe Postcode Districts
            </span>
            <p className="text-sm text-foreground font-medium leading-relaxed">
              We cover postcode districts <strong className="font-bold text-foreground">HP10–HP15</strong> as the primary High Wycombe postcode districts relevant to the town and its immediate surrounding area (HP10, HP11, HP12, HP13, HP14, and HP15).
            </p>
            <p className="text-xs text-muted-foreground">
              Please note: postcode district HP16 is not included as High Wycombe.
            </p>
          </div>

          <div className="lg:col-span-6 space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
              Nearby South Buckinghamshire Areas
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild variant="outline" size="sm" className="bg-card hover:bg-accent">
                <Link to="#">
                  <span>Marlow</span>
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="bg-card hover:bg-accent">
                <Link to="#">
                  <span>Beaconsfield</span>
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="bg-card hover:bg-accent">
                <Link to="#">
                  <span>Amersham</span>
                </Link>
              </Button>
              <Button asChild variant="ghost" size="sm" className="text-primary hover:text-primary/90 font-medium">
                <Link to="#">
                  <span>View all areas</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Two Main Subsections: Residential & Commercial */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
        {/* Residential Subsection */}
        <motion.div variants={itemAnimation} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="h-full">
          <Card className="h-full border-border bg-card flex flex-col justify-between hover:shadow-md transition-[box-shadow,border-color]">
            <CardHeader className="pb-4">
              <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Home className="size-6" />
              </div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <CardTitle className="text-2xl font-bold font-default">
                  Residential scaffolding in High Wycombe
                </CardTitle>
              </div>
              <CardDescription className="text-base text-muted-foreground">
                Reliable access setups for homeowners, local builders, and decorating teams across domestic estates and private residences.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4 pb-6">
              <div className="relative rounded-lg overflow-hidden border border-border mb-4 aspect-video">
                {loading ? <div className="object-cover w-full h-full bg-muted animate-pulse" /> : resImgSrc ? <img src={resImgSrc} data-wvc-srcset="" data-wvc-sizes="" alt={resAlt} loading="lazy" decoding="async" className="object-cover w-full h-full" /> : <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_scaffold_wycombe.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_scaffold_wycombe.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_scaffold_wycombe.png-wvc-sizes" alt="Residential scaffolding installation on a domestic house in High Wycombe" loading="lazy" decoding="async" className="object-cover w-full h-full" />}
                <div className="absolute top-3 right-3">
                  <Badge variant="secondary" className="shadow-xs font-medium">
                    Domestic & Private
                  </Badge>
                </div>
              </div>

              <div className="space-y-2.5">
                {residentialFeatures.map((feat, i) => <div key={feat} data-index={i} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </div>)}
              </div>
            </CardContent>

            <CardFooter className="pt-2 border-t border-border flex flex-wrap gap-3">
              <Button asChild variant="default" size="sm">
                <Link to="/ResidentialScaffoldingPage">
                  <span>Residential Scaffolding</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/ChimneyScaffoldingPage">
                  <span>Chimney Scaffolding</span>
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </motion.div>

        {/* Commercial Subsection */}
        <motion.div variants={itemAnimation} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="h-full">
          <Card className="h-full border-border bg-card flex flex-col justify-between hover:shadow-md transition-[box-shadow,border-color]">
            <CardHeader className="pb-4">
              <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Building2 className="size-6" />
              </div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <CardTitle className="text-2xl font-bold font-default">
                  Commercial scaffolding in High Wycombe
                </CardTitle>
              </div>
              <CardDescription className="text-base text-muted-foreground">
                Robust, engineered scaffold solutions for main contractors, commercial landlords, retail properties, and live occupied sites.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4 pb-6">
              <div className="relative rounded-lg overflow-hidden border border-border mb-4 aspect-video">
                {loading ? <div className="object-cover w-full h-full bg-muted animate-pulse" /> : commImgSrc ? <img src={commImgSrc} data-wvc-srcset="" data-wvc-sizes="" alt={commAlt} loading="lazy" decoding="async" className="object-cover w-full h-full" /> : <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffold_wycombe.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffold_wycombe.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffold_wycombe.png-wvc-sizes" alt="Commercial scaffolding setup on High Wycombe town centre building" loading="lazy" decoding="async" className="object-cover w-full h-full" />}
                <div className="absolute top-3 right-3">
                  <Badge variant="secondary" className="shadow-xs font-medium">
                    Commercial & Industrial
                  </Badge>
                </div>
              </div>

              <div className="space-y-2.5">
                {commercialFeatures.map((feat, i) => <div key={feat} data-index={i} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </div>)}
              </div>
            </CardContent>

            <CardFooter className="pt-2 border-t border-border flex flex-wrap gap-3">
              <Button asChild variant="default" size="sm">
                <Link to="/CommercialScaffoldingPage">
                  <span>Commercial Scaffolding</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/MainContractorsDevelopersPage">
                  <span>Main Contractors & Developers</span>
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </motion.div>
      </div>

      {/* Roofing & Trade Access Block */}
      <motion.div variants={itemAnimation} initial="hidden" whileInView="show" viewport={{
      once: true,
      amount: 0.1
    }} className="rounded-2xl bg-secondary text-secondary-foreground p-6 sm:p-8 lg:p-10">
        <div className="max-w-2xl mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/20 text-primary-foreground text-xs font-medium mb-3">
            <ShieldCheck className="size-3.5" />
            <span>Specialist Access Solutions</span>
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold font-default text-secondary-foreground mb-3">
            Roofing & trade access in High Wycombe
          </h3>
          <p className="text-secondary-foreground/80 text-sm sm:text-base leading-relaxed">
            Safe, certified work platforms designed specifically for roofing contractors, solar installers, and trades requiring edge protection and weatherproofing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {roofingTradeFeatures.map((item, index) => {
          const IconComp = item.icon;
          return <div key={item.title} data-index={index} className="bg-card text-card-foreground p-6 rounded-xl border border-border flex flex-col justify-between">
                <div>
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <IconComp className="size-5" />
                  </div>
                  <h4 className="text-lg font-bold font-default text-card-foreground mb-2">
                    {item.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                    {item.description}
                  </p>
                </div>

                <Button asChild variant="outline" size="sm" className="w-full justify-between">
                  <Link to={item.to}>
                    <span>Explore {item.title}</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>;
        })}
        </div>
      </motion.div>

      {/* Bottom Call to Action Bar */}
      <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 p-6 rounded-xl bg-card border border-border shadow-xs">
        <div className="flex items-center gap-4">
          <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Clock className="size-5" />
          </div>
          <div>
            <p className="font-semibold text-foreground text-base">
              Planning a project in High Wycombe or South Bucks?
            </p>
            <p className="text-sm text-muted-foreground">
              Get in touch for a site visit, technical advice, or an itemised quotation.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <Button asChild variant="default" className="w-full sm:w-auto">
            <Link to="/ContactRequestQuotePage">
              <span>Request a quote</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" className="w-full sm:w-auto">
            <Link to="/ProjectsCaseStudiesPage">
              <span>View projects</span>
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function WycombeCoverage() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 50
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="highwycombescaffoldingcoverage" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <PostsProvider wp_query={{
      post_type: "site_photo",
      per_page: 50,
      status: ["publish"]
    }}>
        <WycombeCoverageContent />
      </PostsProvider>
    </section>;
}